import React, { ComponentProps, PropsWithChildren, useContext } from 'react'
import styled from 'styled-components'
import { rem } from 'polished'
import cn from 'clsx'

import { mediaQueryUp } from 'components/utils/breakpoint'
import { DrawerBehaviourStateContext } from './context/DrawerBehaviourContexts'
import { stopPropagation } from 'lib/eventHandlers'
import TextButton from 'components/Luxkit/Button/TextButton'

export const DrawerFooterContainer = styled.footer`
  background-color: ${({ theme }) => theme.palette.neutral.eight};
  display: flex;
  flex-direction: column;
  justify-content: flex-end;
  padding: ${rem(20)};
  transition: box-shadow .2s ease;
  z-index: 1;

  ${mediaQueryUp.sm} {
    padding: ${rem(16)} ${rem(24)} ${rem(24)};
  }

  > * + * {
    margin-top: ${rem(8)};
  }

  &.has-shadow {
    box-shadow: ${({ theme }) => theme.shadow.top.small};
  }
`

export const DrawerFooterButtonBlocks = styled.div`
  display: inline-flex;
  flex-direction: row;
  gap: ${rem(8)};

  > button {
    flex: 1;
  }
`

export interface DrawerFooterActionProps extends Omit<ComponentProps<typeof TextButton>, 'kind' | 'variant' | 'size'> {}

export interface DrawerFooterProps {
  isPrimaryActionHidden?: boolean
  isSecondaryActionHidden?: boolean
  primaryActionProps?: DrawerFooterActionProps
  secondaryActionProps?: DrawerFooterActionProps
}

function DrawerFooter(props: PropsWithChildren<DrawerFooterProps>) {
  const {
    children,
    isPrimaryActionHidden,
    isSecondaryActionHidden,
    primaryActionProps,
    secondaryActionProps,
  } = props

  const context = useContext(DrawerBehaviourStateContext)

  return <DrawerFooterContainer
    className={cn('modal-footer', {
      'has-shadow': !(context?.bodyHasReachedEnd ?? true),
    })}
    onClick={stopPropagation}
  >
    {children}
    <DrawerFooterButtonBlocks>
      {secondaryActionProps?.children && !isSecondaryActionHidden && <TextButton
        kind="tertiary"
        size="large"
        data-testid="modal-secondary-action"
        {...secondaryActionProps}
      />}
      {primaryActionProps?.children && !isPrimaryActionHidden && <TextButton
        kind="primary"
        size="large"
        data-testid="modal-primary-action"
        {...primaryActionProps}
      />}
    </DrawerFooterButtonBlocks>
  </DrawerFooterContainer>
}

DrawerFooter.defaultProps = {
  primaryButton: null,
  secondaryButton: null,
}

export default DrawerFooter
